import * as dbInspections from 'serviceWorker/db/inspections/inspections';
import * as dbIssues from 'serviceWorker/db/issues/issues';
import type { InspectionEntity } from 'serviceWorker/repository/inspection/entity';
import { inspectionEntityToModel } from 'serviceWorker/repository/inspection/inspectionEntityToModel';
import { getCurrentUsers } from 'serviceWorker/services/users/getUsers';
import type { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import {
  countInspectionRelatedIssues,
  createInspectionRelatedIssuesCountMap,
} from 'shared/domain/inspection/mapping/countInspectionRelatedIssues';

/** Users and issues are read as stored, never waited for: a read must not block on another entity's download. */
export async function readInspectionModels(): Promise<InspectionModel[]> {
  const [entities, users, issueEntities] = await Promise.all([
    dbInspections.get(),
    getCurrentUsers(),
    dbIssues.get(),
  ]);
  const relatedIssuesCountByInspection = createInspectionRelatedIssuesCountMap(issueEntities);
  return entities.map((entity) => ({
    ...inspectionEntityToModel(entity, users),
    relatedIssuesCount: relatedIssuesCountByInspection.get(entity._id) ?? 0,
  }));
}

async function readInspectionModel(entity: InspectionEntity): Promise<InspectionModel> {
  const [users, issueEntities] = await Promise.all([getCurrentUsers(), dbIssues.getByInspectionId(entity._id)]);
  return {
    ...inspectionEntityToModel(entity, users),
    relatedIssuesCount: countInspectionRelatedIssues(entity._id, issueEntities),
  };
}

/** One stored inspection, archived ones included; `undefined` when it is not stored. */
export async function readStoredInspection(id: string): Promise<InspectionModel | undefined> {
  const entity = await dbInspections.getOne(id);
  return entity ? readInspectionModel(entity) : undefined;
}
